import React from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import { useLocalStorage, LS_KEYS } from '../hooks';
import ROUTES from '../router/constants';

const useApi = (lsKey = LS_KEYS.BACKOFFICE) => {
  const history = useHistory();
  const { removeLSItem, getLSItem } = useLocalStorage(lsKey);

  /**
   * Create axios client
   */
  const [api] = React.useState(() => {
    const client = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
    });

    client.interceptors.request.use(axiosConfig => {
      const auth = getLSItem('auth');

      if (auth && auth.token) {
        return {
          ...axiosConfig,
          headers: { Authorization: `Bearer ${auth.token}` },
        };
      }

      return axiosConfig;
    });

    return client;
  });

  /**
   * Handle api error
   */
  const handleError = React.useCallback(
    error => {
      if (error.message === 'Network Error') {
        return new Error('Erro de comunicação com o servidor');
      }

      const { status } = error.response;
      if (status === 500) {
        return new Error('Ops! algo deu errado. Tente novamente.');
      }

      const { error: message } = error.response.data;
      if (status === 401 || status === 403) {
        removeLSItem('auth');
        if (lsKey === LS_KEYS.SITE) {
          return error;
        }
        history.push(ROUTES.BACKOFFICE.LOGIN);
      }

      return new Error(message);
    },
    [history, lsKey, removeLSItem],
  );

  /**
   * Get
   */
  const get = React.useCallback(
    async (url, config) => {
      try {
        const result = await api.get(url, config);
        return result.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    [api, handleError],
  );

  /**
   * Post
   */
  const post = React.useCallback(
    async (url, data, config) => {
      try {
        const result = await api.post(url, data, config);
        return result.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    [api, handleError],
  );

  /**
   * Put
   */
  const put = React.useCallback(
    async (url, data, config) => {
      try {
        const result = await api.put(url, data, config);
        return result.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    [api, handleError],
  );

  /**
   * Delete
   */
  const remove = React.useCallback(
    async (url, data, config) => {
      try {
        const result = await api.delete(url, data, config);
        return result.data;
      } catch (error) {
        throw handleError(error);
      }
    },
    [api, handleError],
  );

  return { get, post, put, remove };
};

export default useApi;
